import React, { useState, useEffect } from 'react';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';

// Platform Showcase - Interactive demo with glowing sections
export const PlatformShowcase = () => {
  const { theme } = useTheme();
  const [activeView, setActiveView] = useState(0);
  const [glowingSection, setGlowingSection] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const bgColor = theme === 'dark' ? '#0D1117' : '#FFFFFF';
  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const textColor = theme === 'dark' ? '#FFFFFF' : '#1A1A2E';
  const mutedText = theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)';

  // Feature categories that map to sidebar sections
  const features = [
    { id: 'score', label: 'DecX Score & Analysis', category: 'INTELLIGENCE' },
    { id: 'canvas', label: 'Pitch Canvas', category: 'INTELLIGENCE' },
    { id: 'trends', label: 'Industry Trends', category: 'INTELLIGENCE' },
    { id: 'audience', label: 'Audience Insights', category: 'INTELLIGENCE' },
    { id: 'battlecards', label: 'Battle Cards', category: 'ARTEFACTS' },
    { id: 'outreach', label: 'Outreach Sequences', category: 'ARTEFACTS' }
  ];

  // Auto-cycle through views
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveView(prev => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [features.length]);

  // Glow animation for current section
  useEffect(() => {
    setGlowingSection(features[activeView].id);
  }, [activeView]);

  const GlowBox = ({ children, isGlowing, style = {} }) => (
    <motion.div
      animate={{
        boxShadow: isGlowing 
          ? `0 0 20px ${C.secondary}60, 0 0 40px ${C.secondary}30, inset 0 0 20px ${C.secondary}10`
          : 'none',
        borderColor: isGlowing ? C.secondary : borderColor
      }}
      transition={{ duration: 0.5 }}
      style={{
        background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        border: `1px solid ${borderColor}`,
        borderRadius: '12px',
        padding: '16px',
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
    >
      {isGlowing && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at center, ${C.secondary}15 0%, transparent 70%)`,
            pointerEvents: 'none'
          }}
        />
      )}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </motion.div>
  );

  // Score Overview View
  const ScoreView = () => (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1.5fr', gap: isMobile ? '12px' : '20px' }}>
      <GlowBox isGlowing={glowingSection === 'score'}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: isMobile ? '9px' : '10px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: isMobile ? '8px' : '12px' }}>
            AUDIT REPORT
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', justifyContent: 'center' }}>
            {/* Score Gauge */}
            <div style={{ position: 'relative', width: isMobile ? '60px' : '80px', height: isMobile ? '60px' : '80px' }}>
              <svg width={isMobile ? 60 : 80} height={isMobile ? 60 : 80} style={{ transform: 'rotate(-90deg)' }}>
                <circle cx={isMobile ? 30 : 40} cy={isMobile ? 30 : 40} r={isMobile ? 24 : 32} fill="none" stroke={borderColor} strokeWidth={isMobile ? 5 : 6} />
                <motion.circle
                  cx={isMobile ? 30 : 40} cy={isMobile ? 30 : 40} r={isMobile ? 24 : 32} fill="none"
                  stroke="#00D4AA"
                  strokeWidth={isMobile ? 5 : 6}
                  strokeLinecap="round"
                  initial={{ strokeDasharray: '0 201' }}
                  animate={{ strokeDasharray: isMobile ? '108 151' : '140 201' }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                />
              </svg>
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                fontSize: isMobile ? '18px' : '24px', fontWeight: 700, color: '#00D4AA'
              }}>72</div>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 700 }}>TechVenture</div>
              <span style={{
                padding: isMobile ? '3px 8px' : '4px 10px', background: '#00D4AA20', color: '#00D4AA',
                borderRadius: '100px', fontSize: isMobile ? '9px' : '10px', fontWeight: 700, display: 'inline-block', marginTop: '4px'
              }}>Decision Ready</span>
              <div style={{ fontSize: isMobile ? '10px' : '11px', opacity: 0.6, marginTop: '6px' }}>
                14 Slides • B2B SaaS
              </div>
            </div>
          </div>
        </div>
      </GlowBox>

      <GlowBox isGlowing={glowingSection === 'score'}>
        <div style={{ fontSize: isMobile ? '9px' : '10px', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.7, marginBottom: isMobile ? '8px' : '12px' }}>
          TOP ISSUES IDENTIFIED
        </div>
        {['Competitive landscape needs depth', 'Market sizing requires validation', 'Ask slide lacks specificity', 'Traction metrics need context'].slice(0, isMobile ? 3 : 4).map((issue, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: isMobile ? '6px 0' : '8px 0', borderBottom: i < (isMobile ? 2 : 3) ? `1px solid ${borderColor}` : 'none',
              fontSize: isMobile ? '11px' : '12px'
            }}
          >
            <span style={{ color: '#FF6B6B' }}>●</span>
            {issue}
          </motion.div>
        ))}
      </GlowBox>
    </div>
  );

  // Pitch Canvas View
  const CanvasView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '12px' : '16px' }}>
        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 700 }}>Pitch Canvas</div>
        <span style={{ fontSize: isMobile ? '10px' : '11px', color: C.secondary }}>Startup Profile</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '8px' : '12px', marginBottom: isMobile ? '12px' : '16px' }}>
        {[
          { label: 'BUSINESS MODEL', value: 'B2B SaaS' },
          { label: 'FOUNDED', value: '2024' },
          { label: 'INDUSTRY', value: 'FinTech', highlight: true },
          { label: 'STAGE', value: 'Seed' }
        ].map((item, i) => (
          <GlowBox key={i} isGlowing={glowingSection === 'canvas' && item.highlight} style={{ padding: isMobile ? '10px' : '12px' }}>
            <div style={{ fontSize: isMobile ? '8px' : '9px', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.5, marginBottom: '4px' }}>
              {item.label}
            </div>
            <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 700, color: item.highlight ? C.secondary : textColor }}>
              {item.value}
            </div>
          </GlowBox>
        ))}
      </div>
      <GlowBox isGlowing={glowingSection === 'canvas'} style={{ marginBottom: isMobile ? '8px' : '12px' }}>
        <div style={{ fontSize: isMobile ? '8px' : '9px', fontWeight: 700, letterSpacing: '0.1em', color: '#00D4AA', marginBottom: '6px' }}>
          VALUE PROPOSITION
        </div>
        <p style={{ fontSize: isMobile ? '11px' : '12px', lineHeight: 1.5, opacity: 0.9 }}>
          AI-powered financial analytics platform that transforms complex data into actionable insights for SMB finance teams.
        </p>
      </GlowBox>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? '8px' : '12px' }}>
        {[
          { label: 'REVENUE', value: '~$120K', sub: 'ARR' },
          { label: 'FUNDING ASK', value: '$5M', sub: 'Series A', highlight: true },
          { label: 'TRACTION', value: '82%', sub: 'MoM Growth' },
          { label: 'PIPELINE', value: '$720K', sub: 'Qualified' }
        ].map((item, i) => (
          <GlowBox key={i} isGlowing={glowingSection === 'canvas'} style={{ padding: isMobile ? '10px' : '12px', textAlign: 'center' }}>
            <div style={{ fontSize: isMobile ? '8px' : '9px', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.5, marginBottom: '3px' }}>
              {item.label}
            </div>
            <div style={{ fontSize: isMobile ? '14px' : '18px', fontWeight: 700, color: item.highlight ? '#00D4AA' : textColor }}>
              {item.value}
            </div>
            <div style={{ fontSize: isMobile ? '9px' : '10px', opacity: 0.6 }}>{item.sub}</div>
          </GlowBox>
        ))}
      </div>
    </div>
  );

  // Industry Trends View
  const TrendsView = () => (
    <div>
      <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 700, marginBottom: isMobile ? '12px' : '16px' }}>Industry Trends & Analysis</div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '12px' : '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isMobile ? '8px' : '12px' }}>
            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>📈</span>
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 700 }}>Key Trends</span>
          </div>
          {[
            { title: 'AI-First Analytics Wave', tags: ['AI', 'Growth'], color: '#00D4AA' },
            { title: 'Multi-Source Data Integration', tags: ['Architecture'], color: '#8B5CF6' },
            { title: 'SMB Market Expansion', tags: ['Market'], color: '#FFB800' }
          ].slice(0, isMobile ? 2 : 3).map((trend, i) => (
            <GlowBox key={i} isGlowing={glowingSection === 'trends' && i === 0} style={{ marginBottom: isMobile ? '6px' : '10px', padding: isMobile ? '10px' : '12px' }}>
              <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 700, marginBottom: '4px' }}>{trend.title}</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {trend.tags.map((tag, j) => (
                  <span key={j} style={{
                    padding: '2px 6px', background: `${trend.color}20`, color: trend.color,
                    borderRadius: '100px', fontSize: isMobile ? '8px' : '9px', fontWeight: 700
                  }}>{tag}</span>
                ))}
              </div>
            </GlowBox>
          ))}
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: isMobile ? '8px' : '12px' }}>
            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>⚠️</span>
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 700 }}>Key Risks</span>
          </div>
          {[
            { title: 'Enterprise Competition Speed', tags: ['Competitive'], color: '#FF6B6B' },
            { title: 'Long Sales Cycles', tags: ['Execution'], color: '#FFB800' },
            { title: 'Data Privacy Regulations', tags: ['Technical'], color: '#8B5CF6' }
          ].slice(0, isMobile ? 2 : 3).map((risk, i) => (
            <GlowBox key={i} isGlowing={glowingSection === 'trends' && i === 0} style={{ marginBottom: isMobile ? '6px' : '10px', padding: isMobile ? '10px' : '12px' }}>
              <div style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 700, marginBottom: '4px' }}>{risk.title}</div>
              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {risk.tags.map((tag, j) => (
                  <span key={j} style={{
                    padding: '2px 6px', background: `${risk.color}20`, color: risk.color,
                    borderRadius: '100px', fontSize: isMobile ? '8px' : '9px', fontWeight: 700
                  }}>{tag}</span>
                ))}
              </div>
            </GlowBox>
          ))}
        </div>
      </div>
    </div>
  );

  // Battle Cards View
  const BattleCardsView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '12px' : '16px' }}>
        <div style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 700 }}>Battle Cards</div>
        <span style={{ fontSize: isMobile ? '9px' : '10px', opacity: 0.6 }}>COMPETITIVE INTEL</span>
      </div>
      
      <GlowBox isGlowing={glowingSection === 'battlecards'} style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '32px', height: '32px', borderRadius: '8px',
              background: '#00D4AA', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', fontWeight: 700, color: '#000'
            }}>A</div>
            <div>
              <div style={{ fontSize: '10px', opacity: 0.5 }}>VS COMPETITOR</div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>Alpha Analytics</div>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '10px', opacity: 0.5 }}>WIN PROB</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#00D4AA' }}>72%</div>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          {[
            { label: 'THEIR STRENGTH', color: '#00D4AA', text: '$50M+ funding, enterprise relationships' },
            { label: 'THEIR WEAKNESS', color: '#FF6B6B', text: 'Complex implementation, high cost' },
            { label: 'OUR DIFFERENTIATOR', color: C.secondary, text: 'SMB-first, rapid deployment, AI-native' }
          ].map((item, i) => (
            <div key={i} style={{
              background: theme === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)',
              borderRadius: '8px', padding: '10px'
            }}>
              <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', color: item.color, marginBottom: '6px' }}>
                ● {item.label}
              </div>
              <p style={{ fontSize: '11px', lineHeight: 1.5, opacity: 0.8 }}>{item.text}</p>
            </div>
          ))}
        </div>
      </GlowBox>

      <GlowBox isGlowing={false} style={{ background: `${C.secondary}10`, borderColor: `${C.secondary}40` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{
            width: '24px', height: '24px', borderRadius: '50%',
            background: '#00D4AA', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '12px', color: '#000'
          }}>✓</span>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: '#00D4AA', marginBottom: '2px' }}>
              WIN STRATEGY
            </div>
            <p style={{ fontSize: '11px', lineHeight: 1.5, opacity: 0.9 }}>
              Lead with speed-to-value: "Deploy in days, not months. See ROI in your first quarter."
            </p>
          </div>
        </div>
      </GlowBox>
    </div>
  );

  // Audience Insights View
  const AudienceView = () => (
    <div>
      <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '16px' }}>Audience Insights</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <GlowBox isGlowing={glowingSection === 'audience'}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: C.secondary, marginBottom: '12px' }}>
            PRIMARY PERSONA
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${C.secondary}, ${C.primary})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px'
            }}>👤</div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 700 }}>VC Partner</div>
              <div style={{ fontSize: '11px', opacity: 0.6 }}>Series A Focus</div>
            </div>
          </div>
          <div style={{ fontSize: '11px', lineHeight: 1.6, opacity: 0.8 }}>
            Looking for: Clear market opportunity, defensible moat, experienced team, capital efficiency
          </div>
        </GlowBox>

        <GlowBox isGlowing={glowingSection === 'audience'}>
          <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: '#FFB800', marginBottom: '12px' }}>
            WHAT THEY NEED TO SEE
          </div>
          {['Strong unit economics', 'Clear path to $100M ARR', 'Competitive moat', 'Capital efficiency'].map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 0', fontSize: '12px'
            }}>
              <span style={{ color: '#00D4AA' }}>✓</span>
              {item}
            </div>
          ))}
        </GlowBox>
      </div>
    </div>
  );

  // Outreach View
  const OutreachView = () => (
    <div>
      <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: '16px' }}>Outreach Sequences</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {[
          { type: 'Email', subject: 'Quick intro - TechVenture + [Firm Name]', status: 'Ready', day: 'Day 1' },
          { type: 'LinkedIn', subject: 'Connection request with context', status: 'Ready', day: 'Day 2' },
          { type: 'Follow-up', subject: 'Re: TechVenture deck - quick question', status: 'Ready', day: 'Day 5' },
          { type: 'WhatsApp', subject: 'Brief voice note intro', status: 'Draft', day: 'Day 7' }
        ].map((seq, i) => (
          <GlowBox key={i} isGlowing={glowingSection === 'outreach' && i === 0} style={{ padding: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  padding: '4px 10px', background: `${C.secondary}20`, color: C.secondary,
                  borderRadius: '6px', fontSize: '10px', fontWeight: 700
                }}>{seq.day}</span>
                <div>
                  <div style={{ fontSize: '10px', opacity: 0.5 }}>{seq.type}</div>
                  <div style={{ fontSize: '12px', fontWeight: 600 }}>{seq.subject}</div>
                </div>
              </div>
              <span style={{
                padding: '4px 10px',
                background: seq.status === 'Ready' ? '#00D4AA20' : '#FFB80020',
                color: seq.status === 'Ready' ? '#00D4AA' : '#FFB800',
                borderRadius: '100px', fontSize: '10px', fontWeight: 700
              }}>{seq.status}</span>
            </div>
          </GlowBox>
        ))}
      </div>
    </div>
  );

  const views = [ScoreView, CanvasView, TrendsView, AudienceView, BattleCardsView, OutreachView];
  const ActiveViewComponent = views[activeView];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      style={{
        background: bgColor,
        borderRadius: isMobile ? '16px' : '20px',
        border: `1px solid ${borderColor}`,
        boxShadow: theme === 'dark' 
          ? '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(0,130,251,0.1)' 
          : '0 20px 60px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        color: textColor
      }}
      data-testid="platform-showcase"
    >
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '12px 16px' : '16px 20px',
        borderBottom: `1px solid ${borderColor}`,
        background: theme === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            <div style={{ width: isMobile ? '8px' : '10px', height: isMobile ? '8px' : '10px', borderRadius: '50%', background: '#FF5F56' }} />
            <div style={{ width: isMobile ? '8px' : '10px', height: isMobile ? '8px' : '10px', borderRadius: '50%', background: '#FFBD2E' }} />
            <div style={{ width: isMobile ? '8px' : '10px', height: isMobile ? '8px' : '10px', borderRadius: '50%', background: '#27CA40' }} />
          </div>
          <span style={{ fontSize: isMobile ? '11px' : '13px', fontWeight: 700, fontFamily: "'Sansation', sans-serif" }}>
            <span style={{ color: C.secondary }}>Dec</span>X AI
          </span>
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', opacity: 0.7 }}>
            <span>Dashboard</span>
            <span>Upload</span>
            <span style={{ color: C.secondary, fontWeight: 700 }}>Results</span>
          </div>
        )}
      </div>

      {/* Mobile: Feature tabs instead of sidebar */}
      {isMobile && (
        <div style={{
          display: 'flex',
          gap: '6px',
          padding: '12px',
          overflowX: 'auto',
          borderBottom: `1px solid ${borderColor}`,
          WebkitOverflowScrolling: 'touch',
        }}>
          {features.map((feature, i) => (
            <motion.div
              key={feature.id}
              onClick={() => setActiveView(i)}
              animate={{
                background: activeView === i ? `${C.secondary}30` : 'transparent',
              }}
              style={{
                padding: '6px 12px',
                borderRadius: '100px',
                fontSize: '10px',
                fontWeight: activeView === i ? 700 : 500,
                color: activeView === i ? C.secondary : mutedText,
                border: `1px solid ${activeView === i ? C.secondary : borderColor}`,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {feature.label.split(' ')[0]}
            </motion.div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '200px 1fr', minHeight: isMobile ? '300px' : '400px' }}>
        {/* Sidebar - Desktop only */}
        {!isMobile && (
          <div style={{
            borderRight: `1px solid ${borderColor}`,
            padding: '16px',
            background: theme === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)'
          }}>
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.5, marginBottom: '12px' }}>
              REPORT SECTIONS
            </div>
            
            {['INTELLIGENCE', 'ARTEFACTS'].map(category => (
              <div key={category} style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.1em', opacity: 0.4, marginBottom: '8px' }}>
                  {category}
                </div>
                {features.filter(f => f.category === category).map((feature, i) => {
                  const isActive = features[activeView].id === feature.id;
                  return (
                    <motion.div
                      key={feature.id}
                      onClick={() => setActiveView(features.findIndex(f => f.id === feature.id))}
                      animate={{
                        background: isActive ? `${C.secondary}30` : 'transparent',
                        borderColor: isActive ? C.secondary : 'transparent'
                      }}
                      whileHover={{ background: `${C.secondary}15` }}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: isActive ? 700 : 400,
                        color: isActive ? C.secondary : mutedText,
                        cursor: 'pointer',
                        marginBottom: '4px',
                        border: '1px solid transparent',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {feature.label}
                    </motion.div>
                  );
                })}
              </div>
            ))}

            {/* Current Deck Info */}
            <div style={{
              marginTop: '24px', padding: '12px',
              background: theme === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
              borderRadius: '8px', fontSize: '10px'
            }}>
              <div style={{ opacity: 0.5, marginBottom: '8px' }}>CURRENT DECK</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>Score</span><span style={{ fontWeight: 700, color: '#00D4AA' }}>72/100</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>Company</span><span style={{ fontWeight: 700 }}>TechVenture</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Slides</span><span style={{ fontWeight: 700 }}>14</span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div style={{ padding: isMobile ? '16px' : '20px', position: 'relative', overflow: 'hidden' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ActiveViewComponent />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Feature indicator dots */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: '8px',
        padding: isMobile ? '12px' : '16px', borderTop: `1px solid ${borderColor}`
      }}>
        {features.map((_, i) => (
          <motion.div
            key={i}
            onClick={() => setActiveView(i)}
            animate={{
              background: i === activeView ? C.secondary : borderColor,
              scale: i === activeView ? 1.2 : 1
            }}
            style={{
              width: '8px', height: '8px', borderRadius: '50%',
              cursor: 'pointer'
            }}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default PlatformShowcase;
